import { PROGRAM_SECTIONS } from '../constants';
import { healthTopicFromAPIString } from './healthTopicUtils';
import type {
  CareProgram,
  CareProgramAPIResponse,
  ProgramSection,
  Program,
  HealthTopic,
  CareProgramType,
  EnrollmentStatus,
} from '../types';

export const transformCareProgram = (apiProgram: CareProgramAPIResponse): CareProgram | null => {
  try {
    return {
      id: apiProgram.id,
      name: apiProgram.name,
      displayTitle: apiProgram.display_title || apiProgram.name,
      description: apiProgram.description,
      targetAudience: apiProgram.target_audience,
      programLengthDays: apiProgram.program_length_days,
      rolling: apiProgram.rolling,
      tags: apiProgram.tags,
      programType: apiProgram.care_program_type,
      todoTypes: apiProgram.to_do_types,
      averageTaskRate: apiProgram.average_task_rate,
      tableOfContentsUrl: apiProgram.table_of_contents_url,
      mappings: {
        memberFacingName: apiProgram.care_program_mappings.member_facing_name,
        memberFacingDescription: apiProgram.care_program_mappings.member_facing_description,
        healthTopics: apiProgram.care_program_mappings.health_topics || [],
        healthGoals: apiProgram.care_program_mappings.health_goals || [],
        isMemberVisible: apiProgram.care_program_mappings.is_member_visible,
        targetPopulation: apiProgram.care_program_mappings.target_population,
        careArea: apiProgram.care_program_mappings.care_area,
        phase: apiProgram.care_program_mappings.phase,
      },
      subscriptions:
        apiProgram.care_program_subscriptions?.map((sub) => ({
          id: sub.id,
          isEnded: sub.is_ended,
          startDate: sub.start_date,
        })) || [],
      requestId: apiProgram.care_program_request_id,
      availableToAll: apiProgram.available_to_all,
    };
  } catch (error) {
    // Failed to transform care program
    return null;
  }
};

/**
 * Groups care programs into sections by program type
 * Uses API program type directly - API is source of truth
 */
export const groupProgramsBySections = (
  programs: CareProgram[],
  healthTopic: HealthTopic | undefined,
  translate: (value: string) => string,
): ProgramSection[] => {
  const grouped: Record<CareProgramType, Program[]> = {
    'Health Tracker': [],
    'Educational Program': [],
  };

  programs.forEach((program) => {
    const { programType } = program;
    if (programType === 'Health Tracker' || programType === 'Educational Program') {
      const activeSubscription = program.subscriptions?.find((sub) => !sub.isEnded);
      let enrollmentStatus: EnrollmentStatus;
      if (activeSubscription) {
        enrollmentStatus = 'enrolled';
      } else if (program.requestId) {
        enrollmentStatus = 'requested';
      } else {
        enrollmentStatus = 'not_enrolled';
      }

      const uiProgram: Program = {
        id: program.id,
        title: program.mappings.memberFacingName,
        healthTopic:
          healthTopic ?? healthTopicFromAPIString(program.mappings.healthTopics?.[0] ?? ''),
        programType,
        enrollmentStatus,
      };
      grouped[programType].push(uiProgram);
    }
  });

  // Sort programs alphabetically within each type
  (Object.keys(grouped) as CareProgramType[]).forEach((programType) => {
    grouped[programType].sort((a, b) =>
      a.title.localeCompare(b.title, undefined, { sensitivity: 'base' }),
    );
  });

  // Build sections array - Health Tracker first, then Educational
  const sections: ProgramSection[] = [
    {
      programType: 'Health Tracker',
      title: translate(PROGRAM_SECTIONS['Health Tracker'].titleKey),
      description: translate(PROGRAM_SECTIONS['Health Tracker'].descriptionKey),
      programs: grouped['Health Tracker'],
      count: grouped['Health Tracker'].length,
    },
    {
      programType: 'Educational Program',
      title: translate(PROGRAM_SECTIONS['Educational Program'].titleKey),
      description: translate(PROGRAM_SECTIONS['Educational Program'].descriptionKey),
      programs: grouped['Educational Program'],
      count: grouped['Educational Program'].length,
    },
  ];

  return sections.filter((section) => section.count > 0);
};
